import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { TeradataCopySource } from 'app/model/pipeline/copysource/teradata.copysource';
import { ValueAdapter } from '../../value.adapter';
import { Metamodel } from 'app/model/base/model';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { partitionOptionConst } from 'app/registries/consts/metadata-consts';
import { GlobalVariables } from 'app/services/globals';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';
import moment from 'moment-timezone';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(TeradataCopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        query: '*',
        queryTimeout: GlobalVariables.enableTeradataQueryTimeout ? '*' : undefined,
        partitionOption: '*',
        partitionSettings: {
            partitionColumnName: '*',
            partitionUpperBound: '*',
            partitionLowerBound: '*'
        }
    }
})
export class TeradataCopySourceAdapter extends CopySourceAdapter<TeradataCopySource, Model.TeradataSource> {
    protected override async _toResource(copySource: TeradataCopySource): Promise<Model.TeradataSource> {
        const result: Model.TeradataSource = this.getCommonCopySourceProperties(
            copySource,
            Metamodel.modelFor(TeradataCopySource).name
        );
        if (copySource.mode === 'query') {
            result.query = ValueAdapter.toResource(copySource, TeradataCopySource.READER_QUERY);
        }
        if (
            GlobalVariables.enableTeradataQueryTimeout &&
            (TeradataCopySource.QUERY_TIMEOUT.templateValue(copySource) || copySource.queryTimeout)
        ) {
            try {
                result.queryTimeout =
                    typeof copySource.queryTimeout === 'number'
                        ? moment.duration(copySource.queryTimeout, 'minutes').format('hh:mm:ss', {
                              trim: false
                          })
                        : ValueAdapter.toResource(copySource, TeradataCopySource.QUERY_TIMEOUT);
            } catch {
                this._logError('Failed to convert queryTimeout into TimeSpan');
            }
        }
        result.partitionOption = ValueAdapter.toResource(copySource, TeradataCopySource.PARTITION_OPTION);
        if (TeradataCopySource.PARTITION_OPTION.templateValue(copySource) != null) {
            result.partitionSettings = {
                partitionColumnName: ValueAdapter.toResource(copySource, TeradataCopySource.PARTITION_COLUMN_NAME),
                partitionUpperBound: ValueAdapter.toResource(copySource, TeradataCopySource.PARTITION_UPPER_BOUND),
                partitionLowerBound: ValueAdapter.toResource(copySource, TeradataCopySource.PARTITION_LOWER_BOUND)
            };
        } else if (
            !stringIsNullOrEmpty(copySource.partitionOption) &&
            copySource.partitionOption !== partitionOptionConst.none
        ) {
            if (copySource.partitionOption === partitionOptionConst.dyamicRange) {
                result.partitionSettings = {
                    partitionColumnName: ValueAdapter.toResource(copySource, TeradataCopySource.PARTITION_COLUMN_NAME),
                    partitionUpperBound: ValueAdapter.toResource(copySource, TeradataCopySource.PARTITION_UPPER_BOUND),
                    partitionLowerBound: ValueAdapter.toResource(copySource, TeradataCopySource.PARTITION_LOWER_BOUND)
                };
            } else if (copySource.partitionOption === partitionOptionConst.hash) {
                result.partitionSettings = {
                    partitionColumnName: ValueAdapter.toResource(copySource, TeradataCopySource.PARTITION_COLUMN_NAME)
                };
            }
        }
        return result;
    }

    protected override async _toModel(
        resource: Model.TeradataSource,
        copyActivity: CopyActivity
    ): Promise<TeradataCopySource> {
        const result = this.createCopySourceWithCommonProperties(
            TeradataCopySource.MODEL,
            resource,
            copyActivity
        ) as TeradataCopySource;
        if (resource.query) {
            ValueAdapter.toModel(result, TeradataCopySource.READER_QUERY, resource.query);
            result.mode = 'query';
        }
        if (GlobalVariables.enableTeradataQueryTimeout) {
            if (resource.queryTimeout) {
                try {
                    if (typeof resource.queryTimeout === 'string' && !isLogicAppExpression(resource.queryTimeout)) {
                        result.queryTimeout = moment.duration(resource.queryTimeout).asMinutes();
                    } else {
                        ValueAdapter.toModel(result, TeradataCopySource.QUERY_TIMEOUT, resource.queryTimeout);
                    }
                } catch {
                    this._logError('Failed to convert queryTimeout into number');
                    result.queryTimeout = undefined;
                }
            } else {
                result.queryTimeout = undefined;
            }
        }
        if (resource.partitionOption != null) {
            ValueAdapter.toModel(result, TeradataCopySource.PARTITION_OPTION, resource.partitionOption);
        }
        if (resource.partitionSettings != null) {
            ValueAdapter.toModel(
                result,
                TeradataCopySource.PARTITION_COLUMN_NAME,
                resource.partitionSettings.partitionColumnName
            );
            ValueAdapter.toModel(
                result,
                TeradataCopySource.PARTITION_LOWER_BOUND,
                resource.partitionSettings.partitionLowerBound
            );
            ValueAdapter.toModel(
                result,
                TeradataCopySource.PARTITION_UPPER_BOUND,
                resource.partitionSettings.partitionUpperBound
            );
        }
        return result;
    }
}
